import { useState } from 'react';
import { useApiModel } from '@hooks/use-api-model';
import { useApiSubmit } from '@hooks/use-api-submit';
import type { IMailingAddress, IPwlJugModel, IPwlJugSubmitResult, TPwlJugMailOption } from '@/types/history/pwl-jug';

const PWL_JUG_ENDPOINT = '/api/register/pwl-jug';

const emptyAddress: IMailingAddress = {
    streetAddress: '',
    city: '',
    state: '',
    zip5: '',
    zip4: '',
    cityStateZip: '',
};

const defaultPwlJugModel: IPwlJugModel = {
    id: 0,
    editKey: '',
    isIE: false,

    status: 'ready',

    displayAddressChoice: false,
    usps: emptyAddress,
    originalEntry: emptyAddress,

    showMentorOffer: false,
    connectToDonorIsDesired: null,

    languageData: {},
};

type TPwlJugResult = IPwlJugSubmitResult & { results?: Partial<IPwlJugModel> };

export const usePwlJug = (registrationId: number, editKey: string) => {
    const [refreshCount, setRefreshCount] = useState(0);

    const url = `${PWL_JUG_ENDPOINT}/${registrationId}/${editKey}`;
    const loadUrl = refreshCount > 0 ? `${url}?refresh=${refreshCount}` : url;

    const { model: loaded, isLoading, error } = useApiModel(loadUrl, defaultPwlJugModel);
    const [override, setOverride] = useState<IPwlJugModel | null>(null);
    const model = override ?? loaded;

    const { submit: post, isSubmitting, submitError } = useApiSubmit<TPwlJugMailOption, Partial<IPwlJugModel>, TPwlJugResult>(
        url,
        (mailOption) => ({
            mailOption,
            usps: mailOption === 'usps' ? model.usps : null,
        }),
        (results) => ({ status: results?.status, results }),
    );

    const submit = (mailOption: TPwlJugMailOption): Promise<IPwlJugSubmitResult> =>
        post(mailOption).then(({ results, ...result }) => {
            if (results) setOverride({ ...defaultPwlJugModel, ...results });

            return result;
        });

    const refresh = () => {
        setOverride(null);
        setRefreshCount((count) => count + 1);
    };

    return { model, isLoading, error, submit, isSubmitting, submitError, refresh };
};
